import styled from 'styled-components';
import { Mapping } from 'api/MappingsAPI';
import { pxToRem } from 'utils/convert';
import { InputText } from 'ui/inputs/inputText/InputText';
import { Search } from 'ui/icons/action/Search';
import {
  BasicSelectWithNestedChildren,
  SelectWrapper,
  BaseOption,
  SelectValueType,
} from 'ui/selects';

import { isDefined } from 'utils/tsHelpers';
import { useIsUserOption } from './hooks/useIsUserOption';
import { useFields } from '../hooks/useFields';
import { Trigger } from '../conditionValue';
import { useSearchFieldContext } from '../context/SearchFieldContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { useConditionFieldNameController, useConditionFieldNameErrors } from '../../../hooks/form';
import { FieldSearchOption } from '../../../types';
import { useResetConditionComparatorAndValue } from '../../../hooks/useResetConditionComparatorAndValue';
import { useFieldsContext } from '../../../contexts';

const FIELD_SELECT_WIDTH = pxToRem(350);

const StyledBasicSelect = styled(BasicSelectWithNestedChildren)`
  ${({ theme }) => theme.styles.body.body2};
  width: fit-content;
  ${SelectWrapper} {
    border: none;
    background: none;
    :hover {
      border-color: none;
    }
  }
`;

export const convertFieldsToOptions = (fields: Mapping[]): BaseOption<string>[] =>
  fields.map(({ fieldName, fieldType }) => ({
    id: `${fieldName}-${fieldType}`,
    value: fieldName,
    label: fieldName,
    Suffix: fieldType,
  }));

export const DATA_TEST = {
  select: 'event-property-select',
};

export const createGetSelection = (contactProperties: Mapping[]) => (values: SelectValueType[]) => {
  const options = contactProperties.filter(({ fieldName }) => fieldName === values[0]);
  const selectedOption = options[0];
  return selectedOption;
};

export const EventPropertySelect = () => {
  const {
    field: { onChange, value: fieldName },
  } = useConditionFieldNameController();
  const { t } = useTranslation();
  const { option } = useSearchFieldContext();
  const { isLoading } = useFieldsContext();
  const isUserOption = useIsUserOption();

  const fields = useFields();
  const getSelection = createGetSelection(fields);
  const resetConditionComparatorAndValue = useResetConditionComparatorAndValue();
  const userProfileFieldMappings = convertFieldsToOptions(fields);
  const placeholder = t('selector.eventPropertyPlaceholder');
  const isDisabled = fields.length === 0 && option !== FieldSearchOption.UserProfile;
  const setFieldName = (value: string) => onChange({ target: { value } });
  const errors = useConditionFieldNameErrors();

  if (isUserOption) return null;

  return (
    <StyledBasicSelect
      data-test={DATA_TEST.select}
      isError={!!errors}
      isLoading={isLoading}
      options={userProfileFieldMappings}
      placeholder={placeholder}
      Trigger={Trigger}
      isDisabled={isDisabled}
      defaultValues={[fieldName]}
      width={FIELD_SELECT_WIDTH}
      onSelect={(options) => {
        const selection = getSelection(options);
        if (isDefined(selection)) {
          setFieldName(getSelection(options).fieldName);
          resetConditionComparatorAndValue(getSelection(options).fieldType);
        }
      }}
    >
      <InputText
        type="search"
        autoComplete="off"
        id="user-profile-search"
        Prefix={<Search />}
        placeholder={placeholder}
        value=""
        autoFocus
      />
    </StyledBasicSelect>
  );
};
